import { useEffect, useRef } from "react";

/**
 * One-shot reveal: adds .is-visible the first time the element enters the
 * viewport, then disconnects. Pass `index` to stagger siblings via --i.
 */
export default function useReveal(index = 0) {
	const ref = useRef(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		// HTML stays visible before hydration and if observers are unavailable.
		if (!("IntersectionObserver" in window) || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
			el.classList.add("is-visible");
			return;
		}

		el.style.setProperty("--i", index);
		if (el.getBoundingClientRect().top < window.innerHeight) {
			el.classList.add("is-visible");
			return;
		}
		el.classList.add("reveal-pending");

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					el.classList.remove("reveal-pending");
					el.classList.add("is-visible");
					observer.disconnect();
				}
			},
			{ threshold: 0.15 }
		);

		observer.observe(el);
		return () => {
			observer.disconnect();
			el.classList.remove("reveal-pending");
		};
	}, [index]);

	return ref;
}
